import React, { useCallback, useState, useMemo } from "react"
import * as gqlTypes from "@hydrologiq/gql-experience/graphql"
import { useQueryWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"
import { DEFAULT_PAGE_SIZE } from "@hydrologiq/shared/config"

interface UseOrderActivitiesOptions {
  orderId: string
  fulfilment?: boolean
  initialPageSize?: number
  clientSidePagination?: boolean // NEW: Enable client-side pagination
  skip?: boolean
  cacheFirst?: boolean
}

interface UseOrderActivitiesReturn {
  activities: gqlTypes.Activity[]
  allActivities: gqlTypes.Activity[] // NEW: All activities for context
  pagination: gqlTypes.PaginationMeta | undefined
  loading: boolean
  refreshing: boolean
  error: string | null
  filterOptions: gqlTypes.ActivityFilterOptions | undefined
  fetchPage: (page: number) => Promise<void>
  fetchPageSize: (pageSize: number) => Promise<void>
  refetch: () => Promise<void>
}

export function useOrderActivities({
  orderId,
  fulfilment = false,
  initialPageSize = DEFAULT_PAGE_SIZE,
  clientSidePagination = false, // NEW: Default to server-side for backward compat
  skip = false,
  cacheFirst = true,
}: UseOrderActivitiesOptions): UseOrderActivitiesReturn {
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(initialPageSize)

  // Reset to page 1 when orderId or fulfilmentId changes
  const prevOrderIdRef = React.useRef<string | undefined>(undefined)
  React.useEffect(() => {
    if (prevOrderIdRef.current && prevOrderIdRef.current !== orderId) {
      setCurrentPage(1)
    }
    prevOrderIdRef.current = orderId
  }, [orderId])

  // If client-side pagination, fetch all activities (up to 100)
  // Otherwise use normal pagination
  const queryVariables = clientSidePagination
    ? {
        order: orderId,
        kind: fulfilment ? gqlTypes.ActivityKind.Fulfilment : undefined,
        page: 1,
        per_page: 100, // Fetch all activities
      }
    : {
        order: orderId,
        kind: fulfilment ? gqlTypes.ActivityKind.Fulfilment : undefined,
        page: currentPage,
        per_page: pageSize,
      }

  const {
    data,
    error: queryError,
    refetch: apolloRefetch,
    loading: apolloLoading,
    networkStatus,
  } = useQueryWithEndpoint(
    gqlTypes.GetActivitiesDocument,
    {
      variables: queryVariables,
      fetchPolicy: cacheFirst ? "network-only" : "no-cache",
      nextFetchPolicy: cacheFirst ? "network-only" : "no-cache",
      errorPolicy: "all",
      notifyOnNetworkStatusChange: true,
      skip: !orderId || skip,
    },
    "primary"
  )

  // All activities from server (for context)
  const allActivities = (data?.activities?.data as gqlTypes.Activity[]) || []

  // Client-side pagination
  const { paginatedActivities, clientPagination } = useMemo(() => {
    if (!clientSidePagination) {
      // Server-side: use activities as-is
      return {
        paginatedActivities: allActivities,
        clientPagination: data?.activities?.meta,
      }
    }

    // Client-side: paginate in memory
    const totalCount = allActivities.length
    const totalPages = Math.ceil(totalCount / pageSize)
    const startIdx = (currentPage - 1) * pageSize
    const endIdx = startIdx + pageSize
    const paginated = allActivities.slice(startIdx, endIdx)

    return {
      paginatedActivities: paginated,
      clientPagination: {
        page: currentPage,
        per_page: pageSize,
        total_count: totalCount,
        total_pages: totalPages,
        has_next: currentPage < totalPages,
        has_prev: currentPage > 1,
      } as gqlTypes.PaginationMeta,
    }
  }, [clientSidePagination, allActivities, currentPage, pageSize])

  const fetchPage = useCallback(
    async (page: number) => {
      if (clientSidePagination) {
        // Client-side: just update state
        setCurrentPage(page)
      } else {
        // Server-side: refetch from API
        setCurrentPage(page)
        await apolloRefetch({
          order: orderId,
          page,
          per_page: pageSize,
        })
      }
    },
    [clientSidePagination, apolloRefetch, orderId, pageSize, data]
  )

  const fetchPageSize = useCallback(
    async (newPageSize: number) => {
      setPageSize(newPageSize)
      setCurrentPage(1) // Reset to page 1 when page size changes

      if (!clientSidePagination) {
        // Server-side: refetch with new page size
        await apolloRefetch({
          order: orderId,
          page: 1,
          per_page: newPageSize,
        })
      }
    },
    [clientSidePagination, apolloRefetch, orderId]
  )

  const refetch = useCallback(async () => {
    await apolloRefetch()
  }, [apolloRefetch])

  // Compute refreshing state - true when refetching in background (networkStatus 4)
  const refreshing = networkStatus === 4

  return {
    activities: paginatedActivities, // Paginated for display
    allActivities, // All activities for context calculations
    pagination: clientPagination,
    loading: apolloLoading && (networkStatus === 1 || networkStatus === 2),
    refreshing,
    error: queryError?.message || null,
    filterOptions: data?.activities?.filter_options as gqlTypes.ActivityFilterOptions | undefined,
    fetchPage,
    fetchPageSize,
    refetch,
  }
}
